import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Heart, Star, Globe, Sparkles } from 'lucide-react';
import { images } from '../data/mock';

const Programs = () => {
  const { t } = useLanguage();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const programIcons = [Heart, Star, Globe];
  const programImages = [
    `${BACKEND_URL}/generated_images/haitian_player_1.png`,
    `${BACKEND_URL}/generated_images/haitian_player_2.png`,
    `${BACKEND_URL}/generated_images/haitian_player_7.png`
  ];

  return (
    <section id="programs" className="relative py-24 bg-gradient-to-b from-gray-950 to-gray-900">
      {/* Background Effect */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-10 w-72 h-72 bg-red-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/30 mb-6">
            <Sparkles className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium text-red-400">Programmes d'Impact</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t.programs.title}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t.programs.subtitle}
          </p>
        </div>

        {/* Programs Grid */}
        <div className="space-y-8">
          {t.programs.list.map((program, index) => {
            const Icon = programIcons[index];
            const isEven = index % 2 === 0;

            return (
              <div
                key={index}
                className={`group relative bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden hover:border-blue-500/50 transition-all duration-500 hover:shadow-xl hover:shadow-blue-500/20`}
              >
                <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8`}>
                  {/* Image Side */}
                  <div className="lg:w-2/5 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-red-500/20 z-10 mix-blend-overlay" />
                    <img
                      src={programImages[index]}
                      alt={program.name}
                      className="w-full h-full object-cover min-h-[300px] lg:min-h-[400px] transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>

                  {/* Content Side */}
                  <div className="lg:w-3/5 p-8 sm:p-10 flex flex-col justify-center">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-white">
                        {program.name}
                      </h3>
                    </div>

                    {program.slogan && (
                      <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-red-500/20 border border-red-500/30 mb-4 w-fit">
                        <span className="text-sm font-semibold text-red-300">{program.slogan}</span>
                      </div>
                    )}

                    <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                      {program.description}
                    </p>

                    <div className="space-y-3">
                      {program.highlights.map((highlight, hIndex) => (
                        <div
                          key={hIndex}
                          className="flex items-start space-x-3 group/item"
                        >
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0 group-hover/item:scale-150 transition-transform duration-200" />
                          <span className="text-gray-400 group-hover/item:text-gray-300 transition-colors duration-200">
                            {highlight}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Programs;
